import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useWelcome, useDismissWelcome } from '@/hooks/useWelcome';

/**
 * First-login welcome modal. Shown to users who have redeemed an invite and not
 * yet dismissed it (driven by GET /api/auth/me -> welcome.show). Any close path
 * dismisses permanently; the journey buttons also navigate.
 */
export const WelcomeModal: React.FC = () => {
  const { data } = useWelcome();
  const dismiss = useDismissWelcome();
  const navigate = useNavigate();

  const show = data?.show ?? false;
  const invites = data?.invitesRemaining ?? 0;

  const close = () => dismiss.mutate();
  const goTo = (path: string) => { dismiss.mutate(); navigate(path); };

  return (
    <Dialog open={show} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Welcome to landing.com!</DialogTitle>
          <DialogDescription>
            Launch a beautiful, fully-hosted landing page in minutes — no code, your own
            custom domain. Start by exploring a template below.
          </DialogDescription>
        </DialogHeader>

        {invites > 0 && (
          <p className="text-sm text-muted-foreground">
            You've also got <span className="font-medium text-foreground">{invites} {invites === 1 ? 'invite' : 'invites'}</span> to
            share with your customers.
          </p>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button className="w-full" onClick={() => goTo('/templates')}>
            Explore templates and get started
          </Button>
          <Button variant="secondary" className="w-full" onClick={() => goTo('/domains')}>
            Add a custom domain
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeModal;
